import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  getShapesCenter,
  toAbsolutePoints,
  toRelativeShape,
  rotatePoints,
  scalePoints,
  reflectPointsAcrossLine,
} from '../utils/transformationUtils';
import { generateId, getSnappedPoint, applyOrthogonalConstraint, SNAP_THRESHOLD } from '../utils/drawingUtils';
import { TOOLS } from '../constants/drawingTools';


const SHAPE_METADATA_FIELDS = [
  'createdByUsername',
  'createdAt',
  'updatedByUsername',
  'updatedAt',
];

const OPEN_SHAPE_TYPES = new Set([TOOLS.LINE, TOOLS.MEASURE, TOOLS.POLYLINE, TOOLS.COTATION, TOOLS.ARROW]);

/**
 * Applies a point-transform function to a shape, handling the
 * absolute↔relative conversion automatically.
 * transformFn receives absolute points and must return absolute points.
 */
const transformShape = (shape, transformFn) => {
  if (!shape.points || shape.points.length < 2) return shape;
  const abs = toAbsolutePoints(shape);
  const transformed = transformFn(abs);
  const { x, y, points } = toRelativeShape(transformed);

  const result = { ...shape, x, y, points };

  // Also transform holes (polygon cutouts) - same anchor-relative system
  if (shape.holes && shape.holes.length > 0) {
    const ox = shape.x || 0;
    const oy = shape.y || 0;
    result.holes = shape.holes.map((hole) => {
      const absHole = hole.map((v, i) => (i % 2 === 0 ? v + ox : v + oy));
      const tHole = transformFn(absHole);
      return tHole.map((v, i) => (i % 2 === 0 ? v - x : v - y));
    });
  }

  return result;
};

/**
 * Creates a deep copy of a shape at a given offset, clearing metadata.
 */
const copyShapeAtOffset = (shape, offsetX, offsetY) => {
  const copied = { ...shape };
  SHAPE_METADATA_FIELDS.forEach((f) => delete copied[f]);
  return {
    ...copied,
    id: generateId(),
    x: (shape.x || 0) + offsetX,
    y: (shape.y || 0) + offsetY,
    // Points stay the same - they're relative to x,y which is offset
  };
};

/**
 * Hook for interactive transform operations (copy, symmetry, rotate, scale).
 *
 * Interaction model:
 *   - Move: click a base point, then click destination → shapes translate
 *   - Copy: click a base point, then click to stamp copies (repeatable, ESC exits)
 *   - Symmetry: two clicks define the reflection axis, shapes are reflected
 *   - Rotate: click 1 = pivot, move to preview angle, click 2 = commit
 *   - Scale: click 1 = pivot, move to preview factor, click 2 = commit
 *
 * Snapping: all click positions and mouse preview positions are snapped to
 * existing shape vertices and grid when isSnappingEnabled is true.
 */
export const useInteractiveTransform = (
  shapes,
  selectedShapeIds,
  setShapes,
  stageRef,
  { stageScale = 1, isSnappingEnabled = false, isOrthogonalModeActive = false, imageSize = { width: 0, height: 0 } } = {},
) => {
  const [transformMode, setTransformMode] = useState(null);
  const [transformState, setTransformState] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const [transformSnapIndicator, setTransformSnapIndicator] = useState(null);

  // Guard against double-fire from mouse+touch events on the same interaction
  const lastTransformClickRef = useRef(0);
  const TRANSFORM_DEBOUNCE_MS = 100;

  const isTransformActive = !!transformMode;


  /** Snap a raw position using the project's snap system and orthogonal mode */
  const snap = useCallback(
    (rawPos, referencePos) => {
      if (!rawPos) return { pos: rawPos, visibleSnapPoints: [] };
      let constrainedPos = rawPos;
      if (isOrthogonalModeActive && referencePos) {
        constrainedPos = applyOrthogonalConstraint(rawPos, referencePos, isOrthogonalModeActive);
      }
      if (!isSnappingEnabled) return { pos: constrainedPos, visibleSnapPoints: [] };
      const result = getSnappedPoint(
        constrainedPos, null, shapes, stageScale, SNAP_THRESHOLD,
        null, null, imageSize, isSnappingEnabled,
      );
      return { pos: result.snappedPoint, visibleSnapPoints: result.visibleSnapPoints };
    },
    [shapes, stageScale, imageSize, isSnappingEnabled, isOrthogonalModeActive],
  );

  const startTransform = useCallback(
    (operation) => {
      if (selectedShapeIds.length === 0) return;

      // Toggle off if same mode
      if (transformMode === operation) {
        setTransformMode(null);
        setTransformState(null);
        setMousePos(null);
        setTransformSnapIndicator(null);
        return;
      }

      const selected = shapes.filter((s) => selectedShapeIds.includes(s.id));
      const center = getShapesCenter(selected);

      setTransformMode(operation);
      setTransformState({
        sourceShapes: selected,
        sourceIds: [...selectedShapeIds],
        center,
        point1: null,
        initialDistance: null,
      });
      setMousePos(null);
      setTransformSnapIndicator(null);
    },
    [shapes, selectedShapeIds, transformMode],
  );


  const cancelTransform = useCallback(() => {
    setTransformMode(null);
    setTransformState(null);
    setMousePos(null);
    setTransformSnapIndicator(null);
  }, []);

  const handleTransformPointerDown = useCallback(
    (e) => {
      if (!transformMode || !transformState) return false;

      if (e.evt && e.evt.button === 2) {
        cancelTransform();
        return true;
      }

      // Guard against double-fire from mouse+touch events
      const now = Date.now();
      if (now - lastTransformClickRef.current < TRANSFORM_DEBOUNCE_MS) return true;
      lastTransformClickRef.current = now;

      const stage = stageRef.current;
      if (!stage) return false;
      const rawPos = stage.getRelativePointerPosition();
      if (!rawPos) return false;

      // The orthogonal reference point is naturally point1 if it exists
      const referencePos = transformState.point1 ? transformState.point1 : null;
      const { pos } = snap(rawPos, referencePos);

      // --- MOVE ---
      if (transformMode === 'move') {
        if (!transformState.point1) {
          setTransformState((prev) => ({ ...prev, point1: pos }));
          return true;
        }
        const { sourceIds, point1 } = transformState;
        const offsetX = pos.x - point1.x;
        const offsetY = pos.y - point1.y;
        setShapes((prev) =>
          prev.map((shape) => {
            if (!sourceIds.includes(shape.id)) return shape;
            return { ...shape, x: (shape.x || 0) + offsetX, y: (shape.y || 0) + offsetY };
          }),
        );
        cancelTransform();
        return true;
      }

      // --- COPY ---
      if (transformMode === 'copy') {
        if (!transformState.point1) {
          setTransformState((prev) => ({ ...prev, point1: pos }));
          return true;
        }
        const { sourceShapes, point1 } = transformState;
        const offsetX = pos.x - point1.x;
        const offsetY = pos.y - point1.y;

        const newShapes = sourceShapes.map((s) => copyShapeAtOffset(s, offsetX, offsetY));
        setShapes((prev) => [...prev, ...newShapes]);
        return true;
      }

      // --- SYMMETRY ---
      if (transformMode === 'symmetry') {
        if (!transformState.point1) {
          setTransformState((prev) => ({ ...prev, point1: pos }));
          return true;
        }
        const { sourceIds, point1 } = transformState;
        setShapes((prev) =>
          prev.map((shape) => {
            if (!sourceIds.includes(shape.id)) return shape;
            return transformShape(shape, (abs) =>
              reflectPointsAcrossLine(abs, point1, pos),
            );
          }),
        );
        cancelTransform();
        return true;
      }

      // --- ROTATE ---
      if (transformMode === 'rotate') {
        if (!transformState.point1) {
          setTransformState((prev) => ({ ...prev, point1: pos }));
          return true;
        }
        const { sourceIds, center, point1 } = transformState;
        const baseAngle = Math.atan2(center.y - point1.y, center.x - point1.x);
        const curAngle = Math.atan2(pos.y - point1.y, pos.x - point1.x);
        const angleDeg = ((curAngle - baseAngle) * 180) / Math.PI;

        setShapes((prev) =>
          prev.map((shape) => {
            if (!sourceIds.includes(shape.id)) return shape;
            return transformShape(shape, (abs) =>
              rotatePoints(abs, angleDeg, point1),
            );
          }),
        );
        cancelTransform();
        return true;
      }

      // --- SCALE ---
      if (transformMode === 'scale') {
        if (!transformState.point1) {
          const { center } = transformState;
          const dist = Math.max(Math.hypot(center.x - pos.x, center.y - pos.y), 1);
          setTransformState((prev) => ({ ...prev, point1: pos, initialDistance: dist }));
          return true;
        }
        const { sourceIds, point1, initialDistance } = transformState;
        const curDist = Math.hypot(pos.x - point1.x, pos.y - point1.y);
        const factor = curDist / initialDistance;

        setShapes((prev) =>
          prev.map((shape) => {
            if (!sourceIds.includes(shape.id)) return shape;
            return transformShape(shape, (abs) =>
              scalePoints(abs, factor, point1),
            );
          }),
        );
        cancelTransform();
        return true;
      }

      return false;
    },
    [transformMode, transformState, stageRef, setShapes, cancelTransform, snap],
  );

  const handleTransformPointerMove = useCallback(
    (e) => {
      if (!transformMode || !transformState) return;
      const stage = stageRef.current;
      if (!stage) return;
      const rawPos = stage.getRelativePointerPosition();
      if (!rawPos) return;

      const referencePos = transformState.point1 ? transformState.point1 : null;
      const { pos } = snap(rawPos, referencePos);
      setMousePos(pos);

      // Update snap indicator
      if (pos.x !== rawPos.x || pos.y !== rawPos.y) {
        setTransformSnapIndicator({ x: pos.x, y: pos.y, visible: true });
      } else {
        setTransformSnapIndicator(null);
      }
    },
    [transformMode, transformState, stageRef, snap],
  );

  /**
   * Compute preview ghost shapes and guide lines.
   * All ghost points are in absolute canvas coords for direct rendering.
   */
  const transformPreview = useMemo(() => {
    if (!transformMode || !transformState || !mousePos) return null;

    const { sourceShapes, center, point1 } = transformState;

    const makeGhosts = (absTransformFn) =>
      sourceShapes
        .filter((s) => s.points && s.points.length >= 4)
        .map((s) => {
          const abs = toAbsolutePoints(s);
          return {
            points: absTransformFn(abs),
            closed: !OPEN_SHAPE_TYPES.has(s.type),
            stroke: s.stroke,
          };
        });

    if (transformMode === 'move') {
      if (!point1) return { pivot: mousePos };
      const offsetX = mousePos.x - point1.x;
      const offsetY = mousePos.y - point1.y;
      return {
        pivot: point1,
        line: { from: point1, to: mousePos },
        ghostShapes: makeGhosts((abs) =>
          abs.map((v, i) => (i % 2 === 0 ? v + offsetX : v + offsetY)),
        ),
      };
    }

    if (transformMode === 'copy') {
      if (!point1) return { pivot: mousePos };
      const offsetX = mousePos.x - point1.x;
      const offsetY = mousePos.y - point1.y;
      return {
        pivot: point1,
        line: { from: point1, to: mousePos },
        ghostShapes: makeGhosts((abs) =>
          abs.map((v, i) => (i % 2 === 0 ? v + offsetX : v + offsetY)),
        ),
      };
    }

    if (transformMode === 'symmetry') {
      if (!point1) return { pivot: mousePos };
      return {
        pivot: point1,
        line: { from: point1, to: mousePos },
        ghostShapes: makeGhosts((abs) =>
          reflectPointsAcrossLine(abs, point1, mousePos),
        ),
      };
    }

    if (transformMode === 'rotate') {
      if (!point1) return { pivot: mousePos };
      const baseAngle = Math.atan2(center.y - point1.y, center.x - point1.x);
      const curAngle = Math.atan2(mousePos.y - point1.y, mousePos.x - point1.x);
      const angleDeg = ((curAngle - baseAngle) * 180) / Math.PI;
      return {
        pivot: point1,
        line: { from: point1, to: mousePos },
        ghostShapes: makeGhosts((abs) => rotatePoints(abs, angleDeg, point1)),
        label: `${Math.round(angleDeg)}°`,
      };
    }

    if (transformMode === 'scale') {
      if (!point1) return { pivot: mousePos };
      const { initialDistance } = transformState;
      const curDist = Math.hypot(mousePos.x - point1.x, mousePos.y - point1.y);
      const factor = curDist / Math.max(initialDistance, 1);
      return {
        pivot: point1,
        line: { from: point1, to: mousePos },
        ghostShapes: makeGhosts((abs) => scalePoints(abs, factor, point1)),
        label: `×${factor.toFixed(2)}`,
      };
    }

    return null;
  }, [transformMode, transformState, mousePos]);

  // ESC to cancel
  useEffect(() => {
    if (!transformMode) return;
    const onKey = (e) => { if (e.key === 'Escape') cancelTransform(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [transformMode, cancelTransform]);

  // Auto-cancel when selection is cleared
  useEffect(() => {
    if (transformMode && selectedShapeIds.length === 0) cancelTransform();
  }, [transformMode, selectedShapeIds.length, cancelTransform]);

  return {
    transformMode,
    isTransformActive,
    transformPreview,
    transformSnapIndicator,
    startTransform,
    cancelTransform,
    handleTransformPointerDown,
    handleTransformPointerMove,
  };
};
